// Register.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import loginpageimg from "/loginpage_illustration.svg";
import logo from "/logo_new.svg";
import CompanyRegistrationForm from "./CompanyRegistration";
import { companyService } from "@services/CompanyService";
import { userService } from "@services/UserServices";
import { toastService } from "@services/ToastService";
import Loader from "@shared/components/Loader";
import { LegalDoc, LegalModal } from "@pages/Project/components/LegalModal";

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  termsAccepted: boolean;
  privacyAccepted: boolean;
}

interface Company {
  id: number;
  name: string;
  is_active: boolean;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [companyQuery, setCompanyQuery] = useState("");
  const [suggestions, setSuggestions] = useState<Company[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );

  const [step, setStep] = useState<"register" | "company">("register");
  const [userData, setUserData] = useState<any>(null);

  const [companyFormData, setCompanyFormData] = useState<any>({
    company_role_id: "",
    countryName: "",
    country_id: undefined,
    focus_area_id: undefined,
    website: "",
    staff_number: "",
    needs: [],
    other: "",
    needs_other_guard: "",
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    setValue,
    watch,
  } = useForm<FormValues>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      termsAccepted: false,
      privacyAccepted: false,
    },
  });

  const companyField = watch("company");
  const termsAccepted = watch("termsAccepted");
  const privacyAccepted = watch("privacyAccepted");

  // Legal modal state
  const [legalOpen, setLegalOpen] = useState(false);
  const [legalDoc, setLegalDoc] = useState<LegalDoc | null>(null);

  const openLegal = (doc: LegalDoc) => {
    setLegalDoc(doc);
    setLegalOpen(true);
  };
  const closeLegal = () => {
    setLegalOpen(false);
    setLegalDoc(null);
  };

  //  Fetch company suggestions only when typing and no selection made
  useEffect(() => {
    if (selectedCompanyId) return;

    const delayDebounce = setTimeout(async () => {
      if (companyQuery.trim().length > 0) {
        try {
          const data = await companyService.searchCompanies(companyQuery);
          setSuggestions(data);
          setShowDropdown(true);
        } catch (err) {
          console.error("Error fetching companies", err);
        }
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [companyQuery, selectedCompanyId]);

  useEffect(() => {
    const checkAuth = async () => {
      setLoading(true);
      const response = await userService.checkAuth();
      if (response && response.status === 200) {
        navigate("/assistant");
      }
      setLoading(false);
    };
    checkAuth();
  }, [navigate]);

  const handleSelectCompany = (company: Company) => {
    setValue("company", company.name);
    setCompanyQuery(company.name);
    setSelectedCompanyId(company.id);
    setShowDropdown(false);
  };

  useEffect(() => {
    if (
      (companyField && companyField.trim().length > 0) ||
      companyQuery.trim().length > 0
    ) {
      setError("");
    }
  }, [companyField, companyQuery]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [isLoading, setIsLoading] = useState(false);

  const handleRegisterSubmit = async () => {
    const { firstName, lastName, email } = getValues();

    let companyId = selectedCompanyId;

    setUserData({
      email,
      first_name: firstName,
      last_name: lastName,
    });

    try {
      const data = await companyService.searchCompanies(companyQuery);
      const index = data.findIndex(
        (c: any) => c.name.toLowerCase() === companyQuery.toLowerCase()
      );

      if (index !== -1) {
        const existingCompany = data[index];
        setSelectedCompanyId(existingCompany.id);
        setValue("company", existingCompany.name);
        setCompanyQuery(existingCompany.name);
        companyId = existingCompany.id;

        if (existingCompany.is_active === false) {
          setStep("company");
          return;
        }
      } else {
        setStep("company");
        return;
      }
    } catch (error) {
      console.error("Error searching companies:", error);
    }

    if (!companyId) {
      setStep("company");
      return;
    }

    const body = {
      email,
      first_name: firstName,
      last_name: lastName,
      accepted_terms: true,
    };

    try {
      setIsLoading(true);
      const res = await companyService.registerUserWithCompany(
        companyId as number,
        body
      );
      navigate("/thank-you", { state: { data: res?.masked_email ?? "" } });
    } catch (error: any) {
      console.error("Registration error:", error);
      toastService.error(
        error?.response?.data?.detail ||
          "Registration failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanyRegistrationSubmit = async (companyData: any) => {
    setCompanyFormData(companyData);

    const payload = {
      user: {
        ...userData,
        accepted_terms: true,
        company_role_id: companyData.company_role_id,
      },
      company: {
        ...(selectedCompanyId !== null && { id: selectedCompanyId }),
        name: companyField,
        country_id: companyData.country_id,
        focus_area_id: companyData.focus_area_id,
        website: companyData.website,
        staff_number: companyData.staff_number,
        registration_target: companyData.registration_target,
      },
    };

    try {
      setIsLoading(true);
      await companyService.registerUserWithNewCompany(payload);
      navigate("/thank-you-company");
    } catch (error: any) {
      console.error("Registration error:", error);
      toastService.error(
        error?.response?.data?.detail ||
          "Registration failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const goBackToRegister = () => setStep("register");
  const handleCompanyChange = useCallback(
    (data: any) => setCompanyFormData(data),
    []
  );

  if (step === "company") {
    return (
      <CompanyRegistrationForm
        onSubmit={handleCompanyRegistrationSubmit}
        companyName={companyField}
        goBack={goBackToRegister}
        isLoading={isLoading}
        defaultValues={companyFormData}
        onChange={handleCompanyChange}
      />
    );
  }

  const canSubmit = isValid && termsAccepted && privacyAccepted && !isLoading;

  return (
    <>
      {!loading && (
        <div className="flex h-screen w-full">
          {/* Left side */}
          <div className="w-[60%] relative hidden sm:flex items-center justify-center">
            <img
              src={loginpageimg}
              alt="Workspace Illustration"
              className="absolute w-full h-full object-cover"
            />
            <div className="absolute top-6 left-6 p-4">
              <h2 className="text-lg 2xl:text-xl font-medium text-white">
                The Intuitive Workspace for
                <br />
                Philanthropic Organizations
              </h2>
            </div>
          </div>

          {/* Right side */}
          <div className="w-full sm:w-[40%] flex justify-center items-center py-5 2xl:p-8 bg-white shadow-lg max-h-screen overflow-y-auto">
            <div className="px-6 w-full sm:max-w-[30rem] sm:w-[26rem] 2xl:w-[30rem]  mx-auto mt-0 2xl:mt-6">
              <div className="mb-[20px] 2xl:mb-[30px] flex justify-center items-center">
                <img src={logo} alt="The Ark Logo" className="w-35 2xl:w-35" />
              </div>

              <h1 className="text-xl 2xl:text-3xl font-semibold text-gray-900 mb-6">
                Create account
              </h1>

              <form
                onSubmit={handleSubmit(handleRegisterSubmit)}
                className="w-full"
              >
                <div className="space-y-4 mb-4">
                  <div>
                    <label className="block text-xs 2xl:text-sm font-medium text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register("firstName", {
                        required: "First name is required",
                      })}
                      className="w-full px-4 py-3 border text-xs 2xl:text-sm border-gray-300 rounded-md"
                    />
                    {errors.firstName && (
                      <p className="text-xs 2xl:text-sm text-red-500 mt-1">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs 2xl:text-sm font-medium text-gray-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register("lastName", {
                        required: "Last name is required",
                      })}
                      className="w-full px-4 py-3 border text-xs 2xl:text-sm border-gray-300 rounded-md"
                    />
                    {errors.lastName && (
                      <p className="text-xs 2xl:text-sm text-red-500 mt-1">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs 2xl:text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Invalid email address",
                        },
                      })}
                      className="w-full px-4 py-3 border text-xs 2xl:text-sm border-gray-300 rounded-md"
                    />
                    {errors.email && (
                      <p className="text-xs 2xl:text-sm text-red-500 mt-1">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  <div className="relative" ref={dropdownRef}>
                    <label className="block text-xs 2xl:text-sm font-medium text-gray-700 mb-1">
                      Company <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register("company", {
                        required: "Company is required",
                      })}
                      className="w-full px-4 py-3 border text-xs 2xl:text-sm border-gray-300 rounded-md"
                      onChange={(e) => {
                        const value = e.target.value;
                        setCompanyQuery(value);
                        setSelectedCompanyId(null);
                        setError("");
                        setValue("company", value, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                      autoComplete="off"
                    />

                    {showDropdown && suggestions.length > 0 && (
                      <ul className="absolute z-50 bg-white border mt-1 w-full rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {suggestions.map((company) => (
                          <li
                            key={company.id}
                            onClick={() => handleSelectCompany(company)}
                            className="px-4 py-2 cursor-pointer text-xs 2xl:text-sm hover:bg-gray-100"
                          >
                            {company.name}
                          </li>
                        ))}
                      </ul>
                    )}
                    {errors.company && (
                      <p className="text-xs 2xl:text-sm text-red-500 mt-1">
                        {errors.company.message}
                      </p>
                    )}
                  </div>
                </div>

                {error && (
                  <div className="text-red-500 text-xs 2xl:text-sm mb-4">
                    {error}
                  </div>
                )}

                <div className="space-y-2 mb-4">
                  <label className="flex items-start gap-2 text-xs 2xl:text-sm text-gray-700">
                    <input
                      id="termsAccepted"
                      type="checkbox"
                      {...register("termsAccepted", { required: true })}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300"
                      aria-invalid={!!errors.termsAccepted}
                      aria-describedby="termsAccepted-error"
                    />
                    <span>
                      I agree to the{" "}
                      <a
                        href="/terms"
                        onClick={(e) => {
                          e.preventDefault();
                          openLegal("terms");
                        }}
                        className="text-blue-600 underline"
                      >
                        Terms &amp; Conditions
                      </a>
                      .
                    </span>
                  </label>
                  {errors.termsAccepted && (
                    <p
                      id="termsAccepted-error"
                      className="text-xs 2xl:text-sm text-red-500"
                    >
                      Please accept the Terms &amp; Conditions to continue.
                    </p>
                  )}

                  <label className="flex items-start gap-2 text-xs 2xl:text-sm text-gray-700">
                    <input
                      id="privacyAccepted"
                      type="checkbox"
                      {...register("privacyAccepted", { required: true })}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300"
                      aria-invalid={!!errors.privacyAccepted}
                      aria-describedby="privacyAccepted-error"
                    />
                    <span>
                      I agree to the{" "}
                      {/* Keep href for fallback/SEO; prevent default to open modal */}
                      <a
                        href="/privacy"
                        onClick={(e) => {
                          e.preventDefault();
                          openLegal("privacy");
                        }}
                        className="text-blue-600 underline"
                      >
                        Privacy Policy
                      </a>
                      .
                    </span>
                  </label>
                  {errors.privacyAccepted && (
                    <p
                      id="privacyAccepted-error"
                      className="text-xs 2xl:text-sm text-red-500"
                    >
                      Please accept the Privacy Policy to continue.
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full mt-2 bg-blue-600 text-sm 2xl:text-base text-white py-3 rounded-md cursor-pointer font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? <Loader color={"#fff"} /> : "Register"}
                </button>
              </form>

              <div className="mt-4 text-xs 2xl:text-sm text-center">
                Already have an account?{" "}
                <button
                  onClick={() => navigate("/login")}
                  className="text-blue-600 font-medium"
                >
                  Sign in
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal mounts at root of this component */}
      <LegalModal isOpen={legalOpen} onClose={closeLegal} doc={legalDoc} />
    </>
  );
};

export default Register;
